import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { MantineProvider } from "@mantine/core";
import { ModalsProvider } from "@mantine/modals";
import { Notifications } from "@mantine/notifications";
import { RouterProvider, createBrowserRouter } from "react-router";
import "./styles.css";
import { adminTheme } from "./theme.ts";
import { BotListPage } from "./pages/BotList.tsx";
import { BotDetailPage } from "./pages/BotDetail.tsx";
import { AdminLayout } from "./pages/AdminLayout.tsx";
import { OAuthGuard } from "./pages/OAuthGuard.tsx";
import { OAuthCallback } from "./pages/OAuthCallback.tsx";
import { TikTokOAuthCallback } from "./pages/TikTokOAuthCallback.tsx";
import { OAuthLoggedOut } from "./pages/OAuthLoggedOut.tsx";
import { PaymentInstancesPage } from "./pages/PaymentInstancesPage.tsx";
import { GrantOutboxPage } from "./pages/GrantOutboxPage.tsx";
import { MonitorPage } from "./pages/MonitorPage.tsx";
import { GlobalTextsPage } from "./pages/GlobalTextsPage.tsx";
import { AuditLogPage } from "./pages/AuditLogPage.tsx";
import { AnalyticsOverviewPage } from "./pages/AnalyticsOverviewPage.tsx";
import { RouteError } from "./pages/RouteError.tsx";
import { isAuthError, triggerReLogin } from "./api.ts";

const qc = new QueryClient({
  defaultOptions: {
    queries: {
      // 401/403 不重试（token 已失效，重试无意义），其它错误重试一次。
      retry: (count, err) => !isAuthError(err) && count < 1,
      staleTime: 10_000,
    },
  },
  // 任何 query / mutation 遇鉴权失效统一触发重新登录（guard 防并发重复触发），
  // 避免 token 过期后 Tab 操作只弹 toast、用户卡死须手动刷新。
  queryCache: new QueryCache({
    onError: (err, query) => {
      if (isAuthError(err)) {
        console.warn("[mip-oauth-debug] QueryCache.onError 鉴权失败", {
          queryKey: query.queryKey,
          status: (err as { status?: number }).status,
          code: (err as { code?: string }).code,
          message: (err as Error).message,
          at: new Date().toISOString(),
        });
        triggerReLogin("QueryCache.onError", err);
      }
    },
  }),
  mutationCache: new MutationCache({
    onError: (err, _vars, _ctx, mutation) => {
      if (isAuthError(err)) {
        console.warn("[mip-oauth-debug] MutationCache.onError 鉴权失败", {
          mutationKey: mutation.options.mutationKey,
          status: (err as { status?: number }).status,
          code: (err as { code?: string }).code,
          message: (err as Error).message,
          at: new Date().toISOString(),
        });
        triggerReLogin("MutationCache.onError", err);
      }
    },
  }),
});

// 每个叶子路由都挂 ErrorBoundary：单页渲染异常时在 AdminLayout 内显示友好错误页，
// 保留侧边导航；OAuthGuard / AdminLayout 自身异常则冒泡到根路由的 ErrorBoundary。
const router = createBrowserRouter([
  { path: "/oauth/callback", Component: OAuthCallback, ErrorBoundary: RouteError },
  { path: "/oauth/tiktok/callback", Component: TikTokOAuthCallback, ErrorBoundary: RouteError },
  { path: "/oauth/logged-out", Component: OAuthLoggedOut, ErrorBoundary: RouteError },
  {
    path: "/",
    Component: OAuthGuard,
    ErrorBoundary: RouteError,
    children: [
      {
        path: "",
        Component: AdminLayout,
        children: [
          { index: true, Component: BotListPage, ErrorBoundary: RouteError },
          {
            path: "analytics",
            Component: AnalyticsOverviewPage,
            ErrorBoundary: RouteError,
          },
          // 子路由：/bots/:id/basic、/bots/:id/credentials/sharechat 等
          // splat 由 BotDetail 解析；兼容旧 ?tab= 启动时 redirect
          {
            path: "bots/:id/*",
            Component: BotDetailPage,
            ErrorBoundary: RouteError,
          },
          {
            path: "payment/instances",
            Component: PaymentInstancesPage,
            ErrorBoundary: RouteError,
          },
          {
            path: "payment/grant-outbox",
            Component: GrantOutboxPage,
            ErrorBoundary: RouteError,
          },
          { path: "texts", Component: GlobalTextsPage, ErrorBoundary: RouteError },
          { path: "monitor", Component: MonitorPage, ErrorBoundary: RouteError },
          { path: "audit", Component: AuditLogPage, ErrorBoundary: RouteError },
        ],
      },
    ],
  },
]);

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <MantineProvider theme={adminTheme} defaultColorScheme="light">
      <ModalsProvider>
        <Notifications position="top-right" autoClose={5000} limit={5} />
        <QueryClientProvider client={qc}>
          <RouterProvider router={router} />
        </QueryClientProvider>
      </ModalsProvider>
    </MantineProvider>
  </StrictMode>,
);
